import React, { useState, useEffect } from "react";
import { Mountain, Clock, Shield, Star, Wrench, CheckCircle, Phone, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

const VetratePanoramicheSection = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const vetrateImages = [
    {
      url: "/images/about6.jpeg",
      title: "Palestra con Vista Panoramica",
      description: "Vetrata panoramica per fitness room con vista mozzafiato su lago e natura incontaminata"
    },
    {
      url: "/images/vetrate5.jpg",
      title: "Veranda Elegante con Giardino",
      description: "Pergola bioclimatica con vetrate scorrevoli che aprono completamente su giardino curato"
    },
    {
      url: "/images/serramenti4.jpg",
      title: "Portico Chiuso Panoramico",
      description: "Trasformazione portico in spazio vivibile tutto l'anno con vetrate a pacchetto"
    },
    {
      url: "/images/serramenti3.jpg",
      title: "Vetrata Scorrevole Premium",
      description: "Sistema vetrato con profili minimali e massima trasparenza per continuità indoor-outdoor"
    },
    {
      url: "/images/about10.jpeg",
      title: "Ambiente Outdoor Coperto",
      description: "Spazio esterno protetto con vetrate panoramiche e copertura elegante"
    }
  ];

  // Auto-slide ogni 6 secondi per apprezzare le vedute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % vetrateImages.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [vetrateImages.length]);

  const goToPrevious = () => {
    setCurrentImage((prev) => (prev - 1 + vetrateImages.length) % vetrateImages.length);
  };

  const goToNext = () => {
    setCurrentImage((prev) => (prev + 1) % vetrateImages.length);
  };

  const features = [
    "Vetri temperati di sicurezza spessore 6-10mm",
    "Sistemi scorrevoli silenziosi su cuscinetti",
    "Profili minimali in alluminio anodizzato",
    "Isolamento termico Ug fino a 1.0 W/m²K",
    "Aperture a pacchetto e scorrevoli parallele",
    "Controllo solare con vetri selettivi",
    "Ventilazione naturale con micro-aperture",
    "Design personalizzabile su progetto",
    "Integrazione con domotica e automazione",
    "Resistenza agli agenti atmosferici"
  ];

  const benefits = [
    "Ampliamento spazi abitabili senza permessi",
    "Continuità visiva tra interno ed esterno",
    "Massima luminosità naturale tutto l'anno",
    "Protezione da pioggia, vento e insetti",
    "Aumento valore immobile del 20-30%",
    "Riduzione costi riscaldamento e climatizzazione",
    "Spazi utilizzabili in ogni stagione",
    "Design architettonico di prestigio"
  ];

  const applications = [
    { name: "Porticati", description: "Trasformazione in verande chiuse", icon: "🏠" },
    { name: "Terrazze", description: "Copertura con vetrate panoramiche", icon: "🌿" },
    { name: "Balconi", description: "Ampliamento e protezione totale", icon: "🏛️" },
    { name: "Giardini d'Inverno", description: "Spazi relax con vista natura", icon: "🌸" }
  ];

  return (
    <section id="vetrate-dettagli" className="py-20 lg:py-32 bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <Mountain className="h-16 w-16 text-green-600 mx-auto mb-4" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Vetrate <span className="text-green-600">Panoramiche</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed text-justify">
            <strong>Connessione perfetta tra interno ed esterno.</strong><br />
            Realizziamo vetrate panoramiche e sistemi vetrati per creare continuità visiva, 
            ampliare gli spazi e godere della natura in ogni stagione dell'anno.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Dynamic Image Slider */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl relative">
              {/* Images */}
              {vetrateImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-1200 ease-in-out ${
                    index === currentImage
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-105'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay elegante per vetrate */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                    <div className="absolute bottom-8 left-8 right-8 text-white">
                      <h4 className="text-xl font-bold mb-3">{image.title}</h4>
                      <p className="text-sm text-gray-200 leading-relaxed">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigation Controls */}
              <button
                onClick={goToPrevious}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-green-600/80 hover:bg-green-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-green-600/80 hover:bg-green-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Elegant Dot Indicators */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
                {vetrateImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImage
                        ? 'bg-green-400 scale-125 shadow-lg'
                        : 'bg-white/60 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>

              {/* Image Counter */}
              <div className="absolute top-6 right-6 bg-black/40 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                {currentImage + 1} / {vetrateImages.length}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Key Info Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="text-center">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">5-10</div>
                  <div className="text-sm text-gray-600">Giorni Lavorativi</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Shield className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">3 Anni</div>
                  <div className="text-sm text-gray-600">Garanzia</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Star className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">Su Misura</div>
                  <div className="text-sm text-gray-600">Progettazione</div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Description */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Specialisti in Vetrate Panoramiche</h3>
              <p className="text-gray-700 leading-relaxed mb-6 text-justify">
                Realizziamo <strong>vetrate panoramiche</strong> per creare continuità tra interno ed esterno, 
                ampliare gli spazi abitabili e godere della vista in ogni condizione atmosferica. 
                Le nostre soluzioni trasformano porticati, terrazze e balconi in ambienti vivibili tutto l'anno.
              </p>
              <p className="text-gray-700 leading-relaxed text-justify">
                Dalle <strong>palestre con vista panoramica</strong> alle verande eleganti per il relax, 
                progettiamo e installiamo sistemi vetrati su misura con vetri temperati di sicurezza 
                e profili minimali per massima trasparenza.
              </p>
            </div>

            {/* Applications */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Applicazioni Principali</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {applications.map((app, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{app.icon}</span>
                        <h4 className="font-bold text-gray-900">{app.name}</h4>
                      </div>
                      <p className="text-sm text-gray-600">{app.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Features List */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Caratteristiche Tecniche</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {features.slice(0, 5).map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                {features.slice(5).map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Vantaggi delle Vetrate Panoramiche</h3>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Star className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Trasforma i Tuoi Spazi Esterni!</h3>
              <p className="text-green-100 mb-6">
                Contattaci per un sopralluogo gratuito e scopri come ampliare la tua casa 
                con vetrate panoramiche che ti connettono con la natura.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-green-600 hover:bg-gray-100 font-semibold"
                  onClick={() => window.open('tel:0587346342', '_self')}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Chiamaci: 0587 346342
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-green-600"
                  onClick={() => window.open('https://api.whatsapp.com/send?phone=3477030346', '_blank')}
                >
                  <Mail className="h-5 w-5 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VetratePanoramicheSection;